'use memo';

import { createFileRoute, useParams } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { NotFound } from '@/components/Error/NotFound';
import { HStack, VStack } from '@/components/Layout';
import { AddStepperCompleteDialog } from '@/components/Stepper';
import { ConfirmDeviceGroup } from '@/features/device-group/components/ConfirmDeviceGroup';
import { DEVICE_GROUP_WIZARD_HASH } from '@/features/device-group/constants';
import { useDeviceGroupWizardStore } from '@/features/device-group/hooks/useDeviceGroupWizardStore';
import { useDuplicateDeviceGroupName } from '@/features/device-group/hooks/useDuplicateDeviceGroupName';
import { buildPreviewBody, useDeviceGroupDetail, useUpdateDeviceGroupMutation } from '@/features/device-group/query';
import { mapDetailToCatalogNameById, mapDetailToFormState, mapDetailToSelectedDeviceById } from '@/features/device-group/utils/map-detail-to-form-state';
import Processing from '@/features/import-csv/-components/Processing';
import { defaultNS, useTranslation } from '@/lib/i18n';
import AddStepperNavigation from '../../-components/AddStepperNavigation';
import AddStepperPanelProgress from '../../-components/AddStepperPanelProgress';
import AddStepperStepTitleCard from '../../-components/AddStepperStepTitleCard';
import BasicForm from '../../-components/BasicForm';
import GenreRulesStep from '../../-components/GenreRulesStep';
import useDeviceGroupWizardHash from '../../-hooks/useHash';
export const Route = createFileRoute('/_staff/devices/_form/device-groups/$id/edit/')({
  component: RouteComponent
});
function RouteComponent() {
  const {
    id
  } = useParams({
    from: '/_staff/devices/_form/device-groups/$id/edit/'
  });
  const {
    hash
  } = useDeviceGroupWizardHash();
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.devices.settings.deviceGroups.wizard'
  });
  const {
    data: detail,
    isPending: isDetailPending,
    isError: isDetailError
  } = useDeviceGroupDetail(id);
  const form = useDeviceGroupWizardStore(s => s.form);
  const reset = useDeviceGroupWizardStore(s => s.reset);
  const mergeDisplayNames = useDeviceGroupWizardStore(s => s.mergeDisplayNames);
  const patchForm = useDeviceGroupWizardStore(s => s.patchForm);
  const patchGenreRule = useDeviceGroupWizardStore(s => s.patchGenreRule);
  const hydratedIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!detail || hydratedIdRef.current === id) {
      return;
    }
    reset({
      form: mapDetailToFormState(detail),
      catalogNameById: mapDetailToCatalogNameById(detail),
      selectedDeviceById: mapDetailToSelectedDeviceById(detail)
    });
    hydratedIdRef.current = id;
  }, [detail, id, reset]);

  // detail の再取得時に表示名マップを追従させる（form は hydratedIdRef で保護）
  useEffect(() => {
    if (!detail || hydratedIdRef.current !== id) {
      return;
    }
    mergeDisplayNames({
      catalogNameById: mapDetailToCatalogNameById(detail),
      selectedDeviceById: mapDetailToSelectedDeviceById(detail)
    });
  }, [detail, id, mergeDisplayNames]);
  useEffect(() => {
    return () => {
      reset();
      hydratedIdRef.current = null;
    };
  }, [reset]);
  const baseLink = `/devices/device-groups/${id}/edit`;
  const isDuplicateName = useDuplicateDeviceGroupName(form.name, id);
  const updateMutation = useUpdateDeviceGroupMutation();
  const [isComplete, setIsComplete] = useState(false);
  const isPending = isDetailPending || updateMutation.isPending;
  const handleUpdate = () => {
    updateMutation.mutate({
      id,
      ...buildPreviewBody(form)
    }, {
      onSuccess: () => setIsComplete(true)
    });
  };
  if (isDetailPending && !detail) {
    return <Processing hash={hash} />;
  }
  if (isDetailError || !detail) {
    return <NotFound />;
  }
  return <HStack className="h-[calc(100vh-64px)] w-full gap-9 p-10">
      <AddStepperNavigation baseLink={baseLink} currentHash={hash} isEdit />

      <VStack className="h-full min-w-0 w-full gap-6">
        {isPending && <Processing hash={hash} />}
        {!isPending && <>
            <AddStepperStepTitleCard hash={hash} />
            <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto">
              {hash === DEVICE_GROUP_WIZARD_HASH.FORM && <BasicForm form={form} onChange={patchForm} isDuplicateName={isDuplicateName} />}

              {hash === DEVICE_GROUP_WIZARD_HASH.SET_GENRE_RULES && <GenreRulesStep form={form} onPatchForm={patchForm} onGenreRuleChange={patchGenreRule} />}

              {hash === DEVICE_GROUP_WIZARD_HASH.CONFIRM && <ConfirmDeviceGroup form={form} />}
            </div>

            <AddStepperPanelProgress baseLink={baseLink} currentHash={hash} form={form} isDuplicateName={isDuplicateName} onSubmit={handleUpdate} />
          </>}

        {isComplete && <AddStepperCompleteDialog completeMessage={t('completeDialog.updateMessage')} completeLinkButtonText={t('completeDialog.detailLink')} completeLinkUrl={`/devices/settings/device-groups/${id}`} />}
      </VStack>
    </HStack>;
}